// Client bundle implementation of the tracking context (device ID, user ID, auth token).
//
// On the client there is a single user per process, so a module-level
// variable is sufficient. That is only true in the browser: importing this
// file from anything that runs on the server would share one user's auth
// token across concurrent requests, which is what trackingContext.server's
// AsyncLocalStorage exists to prevent.
//
// This file intentionally does NOT import
// `async_hooks` so the client bundle stays free of the Node builtin.
// See config/rspack/client.{dev,prod}.js for the
// NormalModuleReplacementPlugin that rewrites `@/network/trackingContext`
// to this file in the client build.
//
// Every setter accepts `string | undefined | null` and stores `null`, because
// callers read from optional sources that hand back either form — an absent
// field on `window.__amplitudeExperiments`, a selector with nothing to return.
// Collapsing both on the way in leaves readers one empty value to check rather
// than having to treat `undefined` and `null` as the same thing at every call
// site, and it matches how the SSR context represents "no value" so both sides
// of the facade agree. Note this only collapses the nullish forms: a caller
// holding an empty string (e.g. the auth-token selector, which returns `''`
// when the token is missing or expired) normalizes it before calling in.

let clientDeviceId: string | null = null;
let clientUserId: string | null = null;
let clientAuthToken: string | null = null;

/** CLIENT: Set the device ID globally. Call once on hydration. */
export function setDeviceId(deviceId: string | undefined | null): void {
    clientDeviceId = deviceId ?? null;
}

/** Read the current device ID. Returns null until setDeviceId() is called. */
export function getDeviceId(): string | null {
    return clientDeviceId;
}

/** CLIENT: Set the user ID globally. Call once on hydration. */
export function setUserId(userId: string | undefined | null): void {
    clientUserId = userId ?? null;
}

/** Read the current user ID. Returns null until setUserId() is called. */
export function getUserId(): string | null {
    return clientUserId;
}

/** CLIENT: Set the validated auth token globally. Call on hydration and whenever it changes (login/logout). */
export function setAuthToken(authToken: string | undefined | null): void {
    clientAuthToken = authToken ?? null;
}

/** Read the current validated auth token. Returns null until setAuthToken() is called. */
export function getAuthToken(): string | null {
    return clientAuthToken;
}

/** getUserAgent is server-only; always null on the client. */
export function getUserAgent(): string | null {
    return null;
}
